import { gql } from "@apollo/client";

/**
 * Fetches the authenticated user's role, persona, and permissions from
 * care-management-gql via the `connectCurrentUser` proxy resolver (ACT-1256 D12).
 *
 * The proxy resolver mirrors the connect-gql AcCurrentUser type and is added
 * to care-management-gql's schema on PR #35173. All RBAC decisions in the app
 * derive from this query — the JWT carries no persona/permission claims.
 *
 * NOTE: `connectCurrentUser` requires a valid bearer; a 401 response routes
 * through the shared Apollo errorLink → triggerExpiryWarning (same path as
 * MemberProfilePage auth failures). No separate logout path here.
 */
/**
 * The `currentPersona.clinicalSummaryTemplate { title }` selection carries the
 * active persona's clinical-summary AI-template title (e.g. "Discharge Summary")
 * for the Clinical Summary card header.
 *
 * DEPLOY-ORDER SAFETY: this field is only requested when
 * VITE_FEATURE_CLINICAL_SUMMARY_TEMPLATE_TITLE is enabled. A GraphQL field that
 * is absent from the *deployed* schema fails the WHOLE CurrentUser query at
 * validation time (validation ignores @skip/@include), which would break RBAC.
 * So we OMIT the field from the document entirely when the flag is off, and only
 * turn the flag on for an environment whose care-management-gql has shipped the
 * field (ACT-1295). Falls back to the static "Clinical Summary" header meanwhile.
 */
const CLINICAL_SUMMARY_TEMPLATE_SELECTION = `
        clinicalSummaryTemplate {
          title
        }`;

/**
 * `groupCode`/`groupDisplayName` (persona-groups epic, ACT-1233/ACT-1256) let the
 * switcher show which admin-defined group a persona belongs to.
 *
 * UNCONDITIONAL as of 2026-08-05. These were previously gated on
 * VITE_FEATURE_PERSONA_GROUPS, on the reasoning that a field absent from the
 * *deployed* schema fails the WHOLE CurrentUser query at validation time (validation
 * ignores @skip/@include) and would take RBAC down with it. That gate has been
 * removed deliberately: AstraConnect has not been released to production users yet,
 * so persona groups is not a phased rollout — it ships in the first release, and
 * every environment's care-management-gql is expected to carry the fields.
 *
 * The validation hazard is real but is now handled by DEPLOY ORDER rather than a
 * flag: care-management-gql must ship the persona-groups resolver to an environment
 * BEFORE this app deploys there. As of this change that means ADO PR 36985
 * (pathways-qa → pathways-staging) then 36896 (pathways-staging → master) must be
 * merged and deployed before this lands on `main`. QA already has it.
 *
 * If a future feature needs the old behaviour, CLINICAL_SUMMARY_TEMPLATE_SELECTION
 * above is still the reference pattern for conditionally omitting a selection.
 */
const PERSONA_GROUP_SELECTION = `
        groupCode
        groupDisplayName`;

const currentUserQuery = (includeClinicalSummaryTemplate: boolean) => gql`
  query CurrentUser {
    connectCurrentUser {
      id
      email
      displayName
      role {
        code
        displayName
      }
      personas {
        code
        displayName${PERSONA_GROUP_SELECTION}
      }
      currentPersona {
        code
        displayName${PERSONA_GROUP_SELECTION}
        modules {
          moduleId
          sortOrder
          actions
          dataScope {
            requireIpaMatch
            allowedLineOfBusiness
          }
        }${includeClinicalSummaryTemplate ? CLINICAL_SUMMARY_TEMPLATE_SELECTION : ""}
      }
      permissions {
        value
        permissionType
        permissionMeta {
          ipas
          healthPlans
          lineOfBusiness
        }
      }
    }
  }
`;

/** Default document (persona-group fields always; no clinical-summary template field). */
export const CURRENT_USER = currentUserQuery(false);

/**
 * Build the CurrentUser query, optionally including the clinical-summary template
 * title selection. That one stays gated on its own env flag
 * (VITE_FEATURE_CLINICAL_SUMMARY_TEMPLATE_TITLE) so it is never requested against a
 * backend that hasn't shipped it. The persona-group fields are always included — see
 * PERSONA_GROUP_SELECTION above for why, and for the deploy-order requirement that
 * replaces the old flag.
 */
export const buildCurrentUserQuery = (includeClinicalSummaryTemplate: boolean) =>
  currentUserQuery(includeClinicalSummaryTemplate);

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CurrentUserVars {} // query takes no variables

export interface AcDataScope {
  requireIpaMatch: boolean | null;
  allowedLineOfBusiness: string[] | null;
}

export interface AcPersonaModule {
  moduleId: string;
  sortOrder: number;
  actions: string[] | null;
  dataScope: AcDataScope | null;
}

export interface AcPersonaSummary {
  code: string;
  displayName: string;
  /**
   * Group code the persona belongs to (persona-groups epic). Always requested, so
   * null means the backend has this persona ungrouped. Kept optional so a response
   * from a backend predating the field still types — always optional-chain reads.
   */
  groupCode?: string | null;
  /** Human-readable group name, paired with groupCode. Same optionality. */
  groupDisplayName?: string | null;
}

/** Active persona's clinical-summary template (title only, for the card header). */
export interface AcClinicalSummaryTemplate {
  title: string;
}

export interface AcCurrentPersona {
  code: string;
  displayName: string;
  modules: AcPersonaModule[];
  /**
   * Present only when the template-title feature is enabled AND the backend
   * returned it (persona has an active template). Undefined otherwise — the
   * card header then falls back to "Clinical Summary".
   */
  clinicalSummaryTemplate?: AcClinicalSummaryTemplate | null;
  /** Same optionality/gating as AcPersonaSummary.groupCode — see there. */
  groupCode?: string | null;
  /** Same optionality/gating as AcPersonaSummary.groupDisplayName — see there. */
  groupDisplayName?: string | null;
}

export interface AcRole {
  code: string;
  displayName: string;
}

export interface AcPermission {
  value: string;
  permissionType: string | null;
  permissionMeta: {
    ipas: string[] | null;
    healthPlans: string[] | null;
    lineOfBusiness: string[] | null;
  } | null;
}

export interface CurrentUserResult {
  connectCurrentUser: {
    id: string;
    email: string | null;
    displayName: string | null;
    role: AcRole | null;
    /** All personas assigned to this user. */
    personas: AcPersonaSummary[];
    /** The currently active persona with its server-driven module list. */
    currentPersona: AcCurrentPersona | null;
    /** Flattened permissions — value + permissionType + data-scope meta. */
    permissions: AcPermission[];
  } | null;
}
